import "./FixtureMatch.css";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  ISoccerMatch,
  ISoccerMatchContestant,
  ISoccerMatchListItem,
} from "../../../../types";
import { getMatch } from "../../api";
import { appTournamentConfig, TOURNAMENT_ID } from "../../config/tournament";
import { getTeamInfo, useMatchResult, useNowTick } from "../../hooks";
import { BACKGROUND_LIVE_POLL_MS } from "../../hooks/useScheduleMatches";
import { useAppStore } from "../../stores/useAppStore";
import { kickoffElapsedClock, liveClock } from "../../utils/liveClock";

dayjs.extend(advancedFormat);

interface FixtureMatchProps {
  matchDetails: ISoccerMatchListItem;
}

interface TeamData {
  isTeamToBeDecided: boolean;
  logoImgSrc: string;
  teamName: string;
  score: string;
  isWinner: boolean;
}

export const FixtureMatch = ({ matchDetails }: FixtureMatchProps) => {
  const { t } = useTranslation();
  const matchData = useAppStore((s) => s.matchData);
  const isViewedMatch = !!matchData?.id && matchData.id === matchDetails.id;
  // `[fixture-${id}]` is shared with useSoccerMatch (viewed match) and
  // useScheduleMatches' background poll. On routes where neither is mounted
  // (fixtures-list), poll it here for live rows - same key, one fetch flies.
  const isLiveRow =
    !isViewedMatch && (matchDetails.status as string) === "playing";
  const { data: cachedMatch, dataUpdatedAt: cachedSyncedAt } = useQuery<ISoccerMatch>({
    queryKey: [`fixture-${matchDetails.id}`],
    queryFn: () => getMatch({ matchId: matchDetails.id, liveScore: true }),
    enabled: isLiveRow,
    refetchInterval: BACKGROUND_LIVE_POLL_MS,
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
  const liveSource = isViewedMatch ? matchData : cachedMatch ?? matchDetails;
  const matchResult = useMatchResult({ soccerMatch: liveSource });
  // Tick whenever this row is live — viewed match uses liveClock, others use
  // the kickoff-elapsed fallback (same pattern as the carousel).
  const isLive = (liveSource.status as string) === "playing";
  const now = useNowTick(isLive);

  const {
    finalScoreboard,
    penaltyScoreboard,
    winnerTeam,
    isMatchNotStarted,
    isMatchPlaying,
    isMatchDraw,
    isMatchEnded,
    isPenaltyShootout,
  } = matchResult;

  const formatMatchDate = (dateTime: string) =>
    dayjs(dateTime).format("Do MMM, YY");
  const formatMatchTime = (dateTime: string) => {
    const dateArr = dateTime.split("T");
    if (!dateArr[dateArr.length - 1].length) return t("fixtureTab.tbd");
    return dayjs(dateTime).format("HH:mm");
  };

  const getMatchSummary = () => {
    if (!isMatchEnded) return "";
    if (isMatchDraw) return t("matchResult.matchDraw");
    if (isPenaltyShootout) {
      return t("matchResult.penaltiesScore", {
        homeTeamScore: penaltyScoreboard.home,
        awayTeamScore: penaltyScoreboard.away,
      });
    }
  };

  const getButtonText = () => {
    if (isMatchEnded || isMatchPlaying) return t("matchResult.fullTime");
    return t("matchResult.upcomingMatch");
  };

  const createTeamData = (
    contestant: ISoccerMatchContestant,
    isHome: boolean
  ): TeamData => {
    const teamInfo = getTeamInfo(contestant?.name);
    const isTeamToBeDecided = !contestant?.id;

    return {
      isTeamToBeDecided,
      logoImgSrc: teamInfo.imgSrc,
      teamName:
        contestant?.code ||
        contestant?.shortName ||
        contestant?.name ||
        "TBD",
      score: isHome ? `${finalScoreboard.home}` : `${finalScoreboard.away}`,
      isWinner: Boolean(winnerTeam?.id && contestant?.id === winnerTeam.id),
    };
  };

  const handleLogoLoadError = (
    event: SyntheticEvent<HTMLImageElement, Event>
  ) => {
    event.currentTarget.src = "/images/teamLogos/flag-fallback.png";
  };

  const matchDate = formatMatchDate(matchDetails.matchDateTime);
  const matchTime = formatMatchTime(matchDetails.matchDateTime);
  const matchSummary = getMatchSummary();
  const stageName = matchDetails.series?.name || matchDetails.stage?.name;
  const venue = matchDetails.venue;
  const venueName = venue?.shortName || venue?.name || venue?.longName;
  const isClubTournament = appTournamentConfig[TOURNAMENT_ID].isClubTournament;

  const homeTeam = createTeamData(matchDetails.contestants[0], true);
  const awayTeam = createTeamData(matchDetails.contestants[1], false);

  const renderTeamLogo = (team: TeamData) => {
    if (team.isTeamToBeDecided) {
      return (
        <img
          src="/images/teamLogos/flag-fallback.png"
          alt="TBD"
          height={24}
          width={40}
          className="fixture-match-team-flag"
        />
      );
    }

    return (
      <img
        src={team.logoImgSrc}
        alt={team.teamName}
        height={28}
        width={40}
        onError={handleLogoLoadError}
        className={`fixture-match-team-flag ${
          team.isWinner ? "winner-team-flag" : ""
        } ${isClubTournament ? "club-team-flag" : ""}`}
      />
    );
  };

  const renderMatchStatus = () => {
    if (isMatchNotStarted) {
      return <span className="fixture-match-time">{matchTime}</span>;
    }

    if (isMatchPlaying) {
      // Period-aware liveClock when we have cached match data; wall-clock fallback otherwise.
      const fullSource = liveSource as ISoccerMatch;
      const liveLabel = fullSource.matchDetails?.periodId != null
        ? liveClock(fullSource, cachedSyncedAt, now)
        : kickoffElapsedClock(matchDetails.matchDateTime, now);
      return (
        <div className="fixture-match-live-stack">
          <div className="fixture-match-live-time">
            <span className="fixture-match-live-dot" aria-hidden />
            <span>{liveLabel}</span>
          </div>
          <span className="fixture-match-scoreboard">
            <span>{homeTeam.score}</span>
            <span>{awayTeam.score}</span>
          </span>
        </div>
      );
    }

    return (
      <span className="fixture-match-scoreboard">
        <span>{homeTeam.score}</span>
        <span>{awayTeam.score}</span>
      </span>
    );
  };

  return (
    <div className="fixture-match-wrapper">
      <div className="fixture-match-container">
        {/* Home Team */}
        <div className="fixture-match-team fixture-match-home-team">
          {renderTeamLogo(homeTeam)}
          <span className="fixture-match-team-name">{homeTeam.teamName}</span>
        </div>

        {/* Match Info */}
        <div className="fixture-match-info">
          {!isMatchPlaying && (
            <span className="fixture-match-date">{matchDate}</span>
          )}
          {renderMatchStatus()}
          {matchSummary && (
            <span className="fixture-match-summary">{matchSummary}</span>
          )}
          {stageName && (
            <span className="fixture-match-stage">{`${stageName} Match`}</span>
          )}
          {venueName && (
            <span className="fixture-match-venue">{venueName}</span>
          )}
        </div>

        {/* Away Team */}
        <div className="fixture-match-team fixture-match-away-team">
          {renderTeamLogo(awayTeam)}
          <span className="fixture-match-team-name">{awayTeam.teamName}</span>
        </div>
      </div>

      <button
        className={`fixture-match-btn ${
          isMatchPlaying ? "live" : isMatchNotStarted ? "upcoming" : ""
        }`}
      >
        {getButtonText()}
      </button>
    </div>
  );
};
